import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(RelativeTime)

import Card from 'app/frontend/base/components/Card'
import SeasonSelect from 'app/frontend/base/pages/TeamDashboard/subpages/TeamLoot/components/SeasonSelect'
import {
  usePageInfo,
  useRaidPlans,
  useUserRaidPlansIndex,
  useVisualPlans,
} from 'app/frontend/base/config/queries'
import Difficulty, {
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import Season from 'app/frontend/types/custom/Season'
import { sortEncountersForSeason } from './encounterOrder'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Image, ListChecks, Plus } from 'lucide-react'
import DefaultPlanStar from './DefaultPlanStar'
import { useIsDefaultRaidPlan } from './defaultRaidPlan'
import TeamPlanCompositionSummary from './TeamPlanCompositionSummary'
import LiveSyncedVisualPlanThumbnail from './LiveSyncedVisualPlanThumbnail'
import EncounterSelectorRow from './EncounterSelectorRow'
import { RaidPlanOwner, createRaidPlan } from 'app/frontend/base/api/raid_plans'
import { createVisualPlan } from 'app/frontend/base/api/visual_plans'
import { isVisualPlansEnabledForSeasonId } from '../visualPlansAvailability'

const SELECTED_ENCOUNTER_STORAGE_KEY = 'plansSelectedEncounterId'

const encounterHash = (encounterId: number) => `#encounter-${encounterId}`

const parseEncounterHash = (hash: string) => {
  const match = hash.match(/^#encounter-(\d+)$/)
  return match ? Number(match[1]) : null
}

type BeforePlansData = {
  teamPlanCounts: Record<number, number>
  isLoading: boolean
}

type AfterPlansData = {
  encounter: wowaudit.Encounter
  instance?: wowaudit.Instance
  difficulty: Difficulty
  season: Season
}

type Props = {
  owner: RaidPlanOwner
  basePath: string
  canEdit: boolean
  intro?: ReactNode
  headerActions?: ReactNode
  beforePlans?: (data: BeforePlansData) => ReactNode
  afterPlans?: (data: AfterPlansData) => ReactNode
  encounterOrders?: wowaudit.Team['encounterOrders']
}

const RaidPlansOverviewContent = ({
  owner,
  basePath,
  canEdit,
  intro,
  headerActions,
  beforePlans,
  afterPlans,
  encounterOrders,
}: Props) => {
  const { t } = useTranslation()
  const isDefaultRaidPlan = useIsDefaultRaidPlan()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {
    data: { currentSeason, currentExpansion },
  } = usePageInfo()
  const { difficulties: availableDifficulties } = useDifficulties()

  const [selectedSeason, setSelectedSeason] = useState(() => {
    const storedSeasonId = Number(localStorage.getItem('plansSeasonId'))
    return (
      currentExpansion.seasons.find((season) => season.id === storedSeasonId) ??
      currentSeason
    )
  })

  const [difficulty, setDifficulty] = useState<Difficulty>(() => {
    const storedDifficulty = localStorage.getItem(
      'plansDifficulty',
    ) as Difficulty | null
    if (storedDifficulty && availableDifficulties.includes(storedDifficulty))
      return storedDifficulty
    return availableDifficulties[0]
  })

  const [selectedEncounterId, setSelectedEncounterId] = useState<number | null>(
    () => {
      const fromHash = parseEncounterHash(window.location.hash)
      if (fromHash) return fromHash
      const stored = Number(
        localStorage.getItem(SELECTED_ENCOUNTER_STORAGE_KEY),
      )
      return stored || null
    },
  )

  const isUserOwner = owner.ownerType === 'user'
  const { data: userPlansIndex, isLoading: isLoadingUserPlans } =
    useUserRaidPlansIndex(selectedSeason?.id, difficulty, isUserOwner)
  const { data: ownerRaidPlans, isLoading: isLoadingOwnerPlans } = useRaidPlans(
    isUserOwner ? undefined : owner,
    selectedSeason?.id,
    !isUserOwner,
  )
  const isLoadingPlans = isUserOwner ? isLoadingUserPlans : isLoadingOwnerPlans
  const raidPlans = isUserOwner ? userPlansIndex?.raidPlans : ownerRaidPlans
  const teamPlanCounts = userPlansIndex?.teamPlanCounts ?? {}

  const visualPlansEnabled = isVisualPlansEnabledForSeasonId(selectedSeason?.id)
  const { data: visualPlans, isLoading: isLoadingVisuals } = useVisualPlans(
    owner,
    selectedSeason?.id,
    difficulty,
    visualPlansEnabled,
  )

  const invalidatePlans = async () => {
    await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
    await queryClient.invalidateQueries({ queryKey: ['userRaidPlans'] })
  }

  const createMutation = useMutation({
    mutationFn: async (encounterId: number) => {
      return await createRaidPlan(owner, {
        encounter_id: encounterId,
        difficulty,
        name: t('dashboard.team.plans.default-name'),
        timeline: { actors: [], phase_durations: {} },
      })
    },
    onSuccess: async (plan) => {
      await invalidatePlans()
      await queryClient.invalidateQueries({ queryKey: ['team'] })
      navigate(`${basePath}/${plan.id}`)
    },
  })

  const createVisualMutation = useMutation({
    mutationFn: async (encounterId: number) => {
      return await createVisualPlan(owner, {
        encounter_id: encounterId,
        difficulty,
        name: t('dashboard.team.plans.visuals.default-name'),
        screens: [{ id: crypto.randomUUID(), image_index: 0, shapes: [] }],
      })
    },
    onSuccess: async (visual) => {
      await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
      navigate(`${basePath}/visuals/${visual.id}`)
    },
  })

  const plansByEncounter = useMemo(() => {
    return (raidPlans ?? [])
      .filter((plan) => plan.difficulty === difficulty)
      .reduce<Record<number, RaidPlan[]>>((acc, plan) => {
        if (!acc[plan.encounterId]) acc[plan.encounterId] = []
        acc[plan.encounterId].push(plan)
        return acc
      }, {})
  }, [difficulty, raidPlans])

  const visualsByEncounter = useMemo(() => {
    return (visualPlans ?? [])
      .filter((visual) => visual.difficulty === difficulty)
      .reduce<Record<number, VisualPlan[]>>((acc, visual) => {
        if (!acc[visual.encounterId]) acc[visual.encounterId] = []
        acc[visual.encounterId].push(visual)
        return acc
      }, {})
  }, [difficulty, visualPlans])

  const encounters = useMemo(
    () => sortEncountersForSeason(selectedSeason, difficulty, encounterOrders),
    [difficulty, encounterOrders, selectedSeason],
  )

  const persistSelectedEncounter = (encounterId: number) => {
    setSelectedEncounterId(encounterId)
    localStorage.setItem(SELECTED_ENCOUNTER_STORAGE_KEY, encounterId.toString())
    history.replaceState({}, '', encounterHash(encounterId))
  }

  useEffect(() => {
    if (encounters.length === 0) return

    if (
      selectedEncounterId &&
      encounters.some((encounter) => encounter.id === selectedEncounterId)
    ) {
      if (window.location.hash !== encounterHash(selectedEncounterId)) {
        history.replaceState({}, '', encounterHash(selectedEncounterId))
      }
      return
    }

    const fromHash = parseEncounterHash(window.location.hash)
    const fallbackId =
      fromHash && encounters.some((encounter) => encounter.id === fromHash)
        ? fromHash
        : encounters[0].id
    persistSelectedEncounter(fallbackId)
  }, [encounters, selectedEncounterId])

  const onSeasonChange = (value?: string) => {
    const season = currentExpansion.seasons.find(
      (s) => s.id.toString() === value,
    )
    if (!season) return
    localStorage.setItem('plansSeasonId', season.id.toString())
    setSelectedSeason(season)
  }

  const selectedEncounter = encounters.find(
    (encounter) => encounter.id === selectedEncounterId,
  )
  const selectedInstance = selectedEncounter
    ? selectedSeason.instances.find(
        (instance) => instance.id === selectedEncounter.instanceId,
      )
    : undefined
  const encounterPlans = selectedEncounter
    ? plansByEncounter[selectedEncounter.id] ?? []
    : []
  const encounterVisuals = selectedEncounter
    ? visualsByEncounter[selectedEncounter.id] ?? []
    : []

  return (
    <div className='space-y-4'>
      <h2 className='flex justify-between gap-3 flex-wrap items-center'>
        <span>{t('dashboard.team.plans.title')}</span>
        <div className='flex items-center gap-2 flex-wrap'>
          <SeasonSelect
            className='w-52'
            value={selectedSeason?.id?.toString()}
            setValue={onSeasonChange}
          />
          <Tabs
            value={difficulty}
            onValueChange={(value) => {
              const next = value as Difficulty
              setDifficulty(next)
              localStorage.setItem('plansDifficulty', next)
            }}
          >
            <TabsList>
              {availableDifficulties.map((option) => (
                <TabsTrigger key={option} value={option}>
                  {difficultiesLabels[option]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {headerActions}
        </div>
      </h2>

      {intro}
      {beforePlans?.({ teamPlanCounts, isLoading: isLoadingPlans })}

      <EncounterSelectorRow
        encounters={encounters}
        plansByEncounter={plansByEncounter}
        visualsByEncounter={visualsByEncounter}
        selectedEncounterId={selectedEncounterId}
        isLoading={isLoadingPlans || isLoadingVisuals}
        onSelect={persistSelectedEncounter}
      />

      {selectedEncounter && (
        <div className='space-y-4'>
          <h4 className='mb-2'>{selectedEncounter.name}</h4>

          <Card className='space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <h4 className='flex items-center gap-2 min-w-0 m-0'>
                <ListChecks className='h-5 w-5 shrink-0' />
                <span className='truncate'>
                  {t('dashboard.team.plans.assignment-cooldown-plans')}
                </span>
                <Badge variant='outline' className='shrink-0'>
                  {encounterPlans.length}
                </Badge>
              </h4>
              {canEdit && (
                <Button
                  size='sm'
                  variant='outline'
                  className='flex items-center gap-2'
                  loading={createMutation.isPending || createMutation.isSuccess}
                  onClick={() => createMutation.mutate(selectedEncounter.id)}
                >
                  <Plus className='h-4 w-4' />
                  {t('shared.create-new')}
                </Button>
              )}
            </div>
            <Separator />
            {isLoadingPlans ? (
              <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-3'>
                <Skeleton className='h-[62px]' />
                <Skeleton className='h-[62px]' />
                <Skeleton className='h-[62px]' />
              </div>
            ) : encounterPlans.length === 0 ? (
              <span className='text-sm text-muted-foreground'>
                {t('dashboard.team.plans.empty-encounter', {
                  difficulty: difficultiesLabels[difficulty],
                  encounter: selectedEncounter.name,
                })}
              </span>
            ) : (
              <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-3'>
                {encounterPlans.map((plan) => (
                  <Link
                    to={`${basePath}/${plan.id}`}
                    key={plan.id}
                    className='block min-w-0'
                  >
                    <Card className='p-3 flex flex-col gap-2 hover:border-primary transition-colors h-full w-full min-w-0 overflow-hidden'>
                      <div className='flex items-center justify-between gap-2 min-w-0 w-full'>
                        <div className='flex items-center gap-1.5 truncate font-medium min-w-0'>
                          {isDefaultRaidPlan(plan) ? <DefaultPlanStar /> : null}
                          <span className='truncate'>{plan.name}</span>
                        </div>
                        <div className='text-xs text-muted-foreground whitespace-nowrap shrink-0'>
                          Created {dayjs(plan.createdAt).fromNow()}
                        </div>
                      </div>
                      <TeamPlanCompositionSummary
                        timeline={plan.timeline}
                        className='w-full min-w-0'
                      />
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {visualPlansEnabled ? (
            <Card className='space-y-3'>
              <div className='flex items-center justify-between gap-2'>
                <h4 className='flex items-center gap-2 min-w-0 m-0'>
                  <Image className='h-5 w-5 shrink-0' />
                  <span className='truncate'>
                    {t('dashboard.team.plans.visuals.title')}
                  </span>
                  <Badge variant='outline' className='shrink-0'>
                    {encounterVisuals.length}
                  </Badge>
                </h4>
                {canEdit && (
                  <Button
                    size='sm'
                    variant='outline'
                    className='flex items-center gap-2'
                    loading={
                      createVisualMutation.isPending ||
                      createVisualMutation.isSuccess
                    }
                    onClick={() =>
                      createVisualMutation.mutate(selectedEncounter.id)
                    }
                  >
                    <Plus className='h-4 w-4' />
                    {t('shared.create-new')}
                  </Button>
                )}
              </div>
              <Separator />
              {isLoadingVisuals ? (
                <div className='grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-4'>
                  <Skeleton className='h-[92px]' />
                  <Skeleton className='h-[92px]' />
                  <Skeleton className='h-[92px]' />
                </div>
              ) : encounterVisuals.length === 0 ? (
                <span className='text-sm text-muted-foreground'>
                  {t('dashboard.team.plans.visuals.empty-encounter', {
                    difficulty: difficultiesLabels[difficulty],
                    encounter: selectedEncounter.name,
                  })}
                </span>
              ) : (
                <div className='grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-4'>
                  {encounterVisuals.map((visual) => (
                    <Link
                      to={`${basePath}/visuals/${visual.id}`}
                      key={visual.id}
                    >
                      <LiveSyncedVisualPlanThumbnail
                        visual={visual}
                        owner={owner}
                      />
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          ) : null}

          {afterPlans ? (
            <div className='pt-6'>
              {afterPlans({
                encounter: selectedEncounter,
                instance: selectedInstance,
                difficulty,
                season: selectedSeason,
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default RaidPlansOverviewContent
